import React, { FC } from 'react';

import IconUser from '@/assets/user.svg?react';

import { FormData, Stages } from '@/api/types';
import BackArrow from '@/assets/back-arrow.svg?react';
import IconCloudDownload from '@/assets/cloud-download.svg?react';
import Logo from '@/assets/logo.svg?react';
import Phone from '@/assets/phone.svg?react';
import { isVehicleDamageProduct } from '@/components/helpers/productHelpers';
import Avatar from '@/components/ui/Avatar';
import Button, { ButtonLoading, ButtonProps } from '@/components/ui/Button';
import { CRM_LINK, PRODUCT, RESPONSIVE_QUERY } from '@/consts';
import MobileMenu, { CaseCardVariants } from '@/features/v2/MobileMenu';
import getHelpfindPhone from '@/helpers/getHelpfindPhone';
import cn from '@/utils/cn';

import { useLingui } from '@lingui/react';
import { useMediaQuery } from 'react-responsive';

export interface NavbarProps
  extends Omit<React.ComponentPropsWithoutRef<'nav'>, 'title'> {
  title?: string | React.ReactNode | null;
  phone?: string | null;
  disableMobileMenu?: boolean;
  compactPhoneButton?: boolean;
  withoutPhone?: boolean;
  data?: FormData;
  caseCardVariant: CaseCardVariants;
  onLogoutClick?: () => void;
  downloadPdfClick?: () => void;
  downloadPdfLoading?: boolean;
  logo?: React.ReactNode;
  phoneColor?: ButtonProps['colors'];
}

const Navbar: FC<NavbarProps> = ({
  data,
  title,
  phone,
  disableMobileMenu = false,
  compactPhoneButton = false,
  withoutPhone = false,
  className,
  caseCardVariant,
  onLogoutClick,
  downloadPdfClick,
  downloadPdfLoading,
  logo,
  phoneColor,
  ...rest
}) => {
  const isDesktop = useMediaQuery({
    query: RESPONSIVE_QUERY.IS_DESKTOP,
  });
  const { i18n } = useLingui();

  const isOfferPage =
    (isVehicleDamageProduct(data?.product?.id) &&
      data?.stage === Stages.STAGE_III) ||
    (data?.product?.id === PRODUCT.HOUSE_HOLD &&
      (data.stage === Stages.OFFER || data.stage === Stages.OFFER_FINISHED));

  const shouldCenterLogo =
    withoutPhone &&
    !data?.crm_user &&
    !onLogoutClick &&
    !downloadPdfClick &&
    caseCardVariant !== CaseCardVariants.CLAIM_PANEL;

  return (
    <nav
      id="navbar"
      tabIndex={-1}
      className={cn(
        'fixed z-50 min-w-full bg-oxford-blue-400 lg:static',
        className
      )}
      {...rest}
    >
      <div
        className={cn(
          'mx-auto flex items-center gap-y-[14px] py-3 lg:p-3',
          'sm:max-w-screen-sm md:max-w-tablet lg:max-w-[980px]',
          'px-5 sm:px-7 lg:px-10',
          shouldCenterLogo ? 'justify-center' : 'justify-between'
        )}
      >
        {logo || <Logo className="h-10 w-[125px] lg:w-[130px]" />}
        <>
          {title && (
            <div
              className={cn(
                'caption-large items-center gap-1 text-dark-gray-100',
                disableMobileMenu ? 'hidden sm:flex' : 'flex'
              )}
            >
              {title}
            </div>
          )}
        </>
        {!disableMobileMenu && !shouldCenterLogo && (
          <span aria-hidden className="w-10 lg:hidden" />
        )}
        {data?.crm_user && (
          <div className={'hidden items-center gap-x-3 lg:flex'}>
            <Avatar
              img={data.crm_user.avatar}
              name={data.crm_user.name}
              variant={'clean'}
              size={'small'}
            />

            <div className="flex flex-col items-start gap-1">
              <span className="caption text-gray-100">
                Witaj, {data.crm_user.name.split(' ')[0].trim()}
              </span>
              <Button size={'xs'} asChild>
                <a
                  href={`${CRM_LINK}/leads/${data.id}/edit`}
                  target="_blank"
                  className={'caption flex items-center gap-x-0.5 text-white'}
                  rel="noreferrer"
                >
                  <span className="caption">Wróć do CRM</span>
                  <BackArrow className={'size-3'} />
                </a>
              </Button>
            </div>
          </div>
        )}
        {!data?.crm_user &&
          !onLogoutClick &&
          !downloadPdfClick &&
          !withoutPhone &&
          caseCardVariant !== CaseCardVariants.CLAIM_PANEL && (
            <>
              <Button
                asChild
                size={'small'}
                colors={phoneColor}
                className={cn(
                  'lg:py-[11px]',
                  !disableMobileMenu && 'hidden lg:flex',
                  compactPhoneButton &&
                    'max-lg:p-3 [&>span]:hidden [&>span]:lg:inline'
                )}
              >
                <a href={`tel:${phone || getHelpfindPhone(data?.product?.id)}`}>
                  <Phone width={18} className="shrink-0" />
                  <span className={cn('hidden')}>
                    {phone || getHelpfindPhone(data?.product?.id)}
                  </span>
                </a>
              </Button>
            </>
          )}
        {onLogoutClick && caseCardVariant === CaseCardVariants.CLAIM_PANEL && (
          <Button
            size={'small'}
            asChild
            className="hidden cursor-pointer lg:flex"
            onClick={onLogoutClick}
          >
            <div>
              <IconUser width={18} className="shrink-0" />
              {i18n.t('Wyloguj się')}{' '}
            </div>
          </Button>
        )}
        {downloadPdfClick && (
          <Button
            onClick={downloadPdfClick}
            className="hidden lg:flex"
            size="small"
            loading={downloadPdfLoading}
          >
            {downloadPdfLoading ? (
              <ButtonLoading />
            ) : (
              <IconCloudDownload className="size-[18px]" />
            )}
            {i18n.t('Pobierz PDF')}
          </Button>
        )}
        {!isDesktop && !disableMobileMenu && (
          <div>
            <MobileMenu
              crmUser={data?.crm_user}
              leadID={data?.id}
              caseCardVariant={caseCardVariant}
              isOfferPage={isOfferPage}
            />
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
